/**
 * API para Registro de Asistencias
 * 
 * Maneja todas las operaciones relacionadas con el registro de asistencias
 * por grupo y fecha. Reutiliza endpoints compartidos con calificaciones.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

/**
 * Obtiene el token de autenticación del localStorage
 */
function getAuthToken() {
  return localStorage.getItem('authToken');
}

/**
 * Headers comunes para las peticiones
 */
function getHeaders() {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

/**
 * Obtiene la información del asesor basado en el usuario autenticado
 * Reutiliza el endpoint de calificaciones
 * @returns {Promise<Object>} Información del usuario, asesor e institución
 */
export async function getInfoAsesorUsuario() {
  try {
    const res = await fetch(`${API_BASE}/escolar/calificaciones/info-asesor/`, {
      headers: getHeaders()
    });
    
    if (!res.ok) {
      console.error('[asistencias.js] Error al obtener info asesor:', res.status);
      throw new Error('Error al obtener información del asesor');
    }
    
    return await res.json();
  } catch (error) {
    console.error('[asistencias.js] Error:', error);
    throw error;
  }
}

/**
 * Obtiene los grupos asignados a un asesor
 * Reutiliza el endpoint de calificaciones
 * @param {Object} params - Parámetros de búsqueda
 * @param {number} params.id_asesor - ID del asesor
 * @param {number} [params.id_campus] - ID del campus (opcional)
 * @param {string} [params.search] - Texto de búsqueda (opcional)
 * @returns {Promise<Object>} Lista de grupos
 */
export async function getGruposAsesor({ id_asesor, id_campus, search }) {
  try {
    const params = new URLSearchParams();
    params.append('id_asesor', id_asesor);
    if (id_campus) params.append('id_campus', id_campus);
    if (search) params.append('search', search);
    
    const res = await fetch(`${API_BASE}/escolar/calificaciones/grupos-asesor/?${params.toString()}`, {
      headers: getHeaders()
    });
    
    if (!res.ok) {
      console.error('[asistencias.js] Error al obtener grupos:', res.status);
      throw new Error('Error al obtener grupos del asesor');
    }
    
    return await res.json();
  } catch (error) {
    console.error('[asistencias.js] Error:', error);
    throw error;
  }
}

/**
 * Obtiene los estudiantes de un grupo con sus asistencias para una fecha
 * @param {number} grupoId - ID del grupo
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @param {number} parcial - Número de parcial (1, 2, 3)
 * @returns {Promise<Object>} Lista de estudiantes con asistencias
 */
export async function getEstudiantesAsistencias(grupoId, fecha, parcial = 1) {
  try {
    const params = new URLSearchParams();
    if (fecha) params.append('fecha', fecha);
    params.append('parcial', parcial);
    
    const url = `${API_BASE}/escolar/asistencias/estudiantes/${grupoId}/?${params.toString()}`;
    console.log('[asistencias.js] Llamando a getEstudiantesAsistencias:', url);
    
    const res = await fetch(url, {
      headers: getHeaders()
    });
    
    console.log('[asistencias.js] Response status:', res.status);
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('[asistencias.js] Error al obtener estudiantes:', res.status, errorData);
      throw new Error(errorData.error || 'Error al obtener estudiantes del grupo');
    }
    
    return await res.json();
  } catch (error) {
    console.error('[asistencias.js] Error:', error);
    throw error;
  }
}

/**
 * Guarda las asistencias de los estudiantes
 * @param {Object} data - Datos de asistencias
 * @param {number} data.id_grupo - ID del grupo
 * @param {string} data.fecha - Fecha en formato YYYY-MM-DD
 * @param {number} data.parcial - Número de parcial
 * @param {Array} data.asistencias - Array con las asistencias de cada estudiante
 * @returns {Promise<Object>} Resultado del guardado
 */
export async function guardarAsistencias(data) {
  try {
    console.log('[asistencias.js] Guardando asistencias:', data);
    
    const res = await fetch(`${API_BASE}/escolar/asistencias/guardar/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    
    const responseData = await res.json();
    
    if (!res.ok) {
      console.error('[asistencias.js] Error al guardar:', res.status, responseData);
      throw new Error(responseData.error || 'Error al guardar asistencias');
    }
    
    console.log('[asistencias.js] Asistencias guardadas:', responseData);
    return responseData;
  } catch (error) {
    console.error('[asistencias.js] Error:', error);
    throw error;
  }
}

/**
 * Obtiene el historial de asistencias de un estudiante
 * @param {number} estudianteGrupoId - ID del estudiante en el grupo
 * @returns {Promise<Object>} Historial de asistencias
 */
export async function getHistorialAsistencias(estudianteGrupoId) {
  try {
    // Por ahora retorna datos vacíos, se puede implementar después
    return { historial: [] };
  } catch (error) {
    console.error('[asistencias.js] Error:', error);
    throw error;
  }
}
